"use client";

import {JSX, useRef} from "react";

import {useSnapshot} from "valtio";

import {type DateFlexibilityInstance} from "@/features/rental";

import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {DateRangePickerField} from "@/shared/ui/form/datepickers";
import {DateRangeFilterTrigger} from "@/shared/ui/form/datepickers/range/parts";
import {RadioField} from "@/shared/ui/form/fields";
import {SelectField} from "@/shared/ui/form/select/select-field";

import "./styles/date-flexibility.scss";

interface DateFlexibilityViewProps {
    model: DateFlexibilityInstance;
    className?: string;
}

export function DateFlexibilityView({model, className}: DateFlexibilityViewProps): JSX.Element {
    const snapshot = useSnapshot(model);
    const selectRef = useRef<HTMLDivElement>(null);

    const {t} = useTranslation("search");
    const dropdownRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => model.close(),
        disableClick: !snapshot.isOpen,
        ignoredRefs: [selectRef],
        ignoredSelectors: [".daterange-picker-field__dropdown", ".select-field__floating-dropdown"],
    });

    return (
        <div ref={dropdownRef} className={`date-flexibility ${className || ""}`}>
            <DateRangePickerField
                model={model.dateRange}
                startPlaceholder={t("checkIn")}
                endPlaceholder={t("checkOut")}
                singleCalendar={true}
                showClearButton={false}
                enableAutoScroll={true}
                dropdownZIndex={20}
                dropdownOffset={5}
                placement="top"
                renderTrigger={(props) => (
                    <DateRangeFilterTrigger
                        {...props}
                        onClick={() => model.toggle()}
                        onClear={() => {
                            props.onClear();
                            model.close();
                        }}
                        startDate={snapshot.committedStartDate}
                        endDate={snapshot.committedEndDate}
                    />
                )}
                disableClickOutside={true}
                controlledIsOpen={snapshot.isOpen}
            >
                {/* Radio buttons for Exact/Flexible */}
                <div className="date-flexibility__switcher">
                    <RadioField model={model.flexibilityRadio} direction="horizontal" size="sm" />
                </div>

                {/* Date error message */}
                {snapshot.dateError && (
                    <Typography as="span" color="error" size={12} className="date-flexibility__error">
                        {snapshot.dateError}
                    </Typography>
                )}

                {/* Nights selector (visible in Flexible mode) */}
                {snapshot.flexibilityRadio.value && (
                    <div ref={selectRef} className="date-flexibility__nights">
                        <SelectField model={model.nightsSelect} placeholder={t("nightsPlaceholder")} label={t("numberOfNights")} />
                        {snapshot.nightsError && (
                            <Typography as="span" color="error" size={12} className="date-flexibility__error">
                                {snapshot.nightsError}
                            </Typography>
                        )}
                        <Typography color="paragraph" as="span">
                            {t("between")}
                        </Typography>
                    </div>
                )}

                {/* Action buttons */}
                <div className="date-flexibility__actions">
                    <ButtonBase
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            model.clear();
                        }}
                    >
                        {t("$.common.clear")}
                    </ButtonBase>
                    <ButtonBase
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            model.applyChanges();
                        }}
                    >
                        {t("$.common.update")}
                    </ButtonBase>
                </div>
            </DateRangePickerField>
        </div>
    );
}
